import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";

const ProjectRankingSection = ({ BASE_URL }) => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const [report, setReport] = useState(null);
  const [allReports, setAllReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // -----------------------------
  // Fetch available projects from backend
  // -----------------------------
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await fetch(`${BASE_URL}/admin/projects-list`, {
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error("Failed to fetch projects");
        const data = await res.json();
        setProjects(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load project list");
      }
    };
    fetchProjects();
  }, [BASE_URL]);


  const getTierText = (tierText) => {
    // Remove all star emojis and extra spaces, leaving only the word part
    return tierText.replace(/⭐+/g, "").trim();
  };


  // -----------------------------
  // Fetch report for selected project
  // -----------------------------
  const handleViewReport = async () => {
    if (!selectedProject) {
      alert("Please select a project first.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      setAllReports([]);
      const res = await fetch(`${BASE_URL}/admin/project-report/${selectedProject}`, {
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to fetch project report");
      const data = await res.json();
      setReport(data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch project report");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // Fetch all projects ranking
  // -----------------------------
  const handleViewAllProjects = async () => {
    try {
      setLoading(true);
      setError("");
      setReport(null);
      const res = await fetch(`${BASE_URL}/admin/all-projects-report`, {
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) throw new Error("Failed to fetch all project rankings");
      const data = await res.json();
      setAllReports(data);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch all project rankings");
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // UI Helpers
  // -----------------------------
  const getStars = (tierText) => {
    const starsMatch = tierText.match(/⭐+/);
    return starsMatch ? starsMatch[0] : "⭐";
  };

  // -----------------------------
  // Component UI
  // -----------------------------
  return (
    <motion.div
      className="bg-gray-800 p-6 rounded-2xl shadow-lg mb-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <h2 className="text-xl font-semibold mb-4 text-center text-indigo-400">
        Project Quality Ranking
      </h2>

      {/* Project Selection + Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="bg-gray-700 text-gray-200 px-4 py-2 rounded-md focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">-- Select Project --</option>
          {projects.map((proj, idx) => (
            <option key={idx} value={proj}>
              {proj}
            </option>
          ))}
        </select>

        <button
          onClick={handleViewReport}
          className="bg-indigo-600 hover:bg-indigo-700 px-6 py-2 rounded-lg font-semibold"
        >
          View Report
        </button>

        <button
          onClick={handleViewAllProjects}
          className="bg-green-600 hover:bg-green-700 px-6 py-2 rounded-lg font-semibold"
        >
          View All Projects Ranking
        </button>
      </div>

      {/* Loading & Error */}
      {loading && <p className="text-gray-400 text-center">Loading...</p>}
      {error && <p className="text-red-500 text-center">{error}</p>}

      {/* Single Project Report */}
      {report && (
        <div className="bg-gray-900 p-5 rounded-xl shadow-md">
          <h3 className="text-2xl font-semibold text-center text-green-400 mb-2">
            {report.project_name}
          </h3>

          <div className="text-center mb-4">
            <p className="text-yellow-400 text-lg">
              Quality Rating:{" "}
              <span className="text-white font-semibold">
                {report.quality_rating.toFixed(2)}%
              </span>
            </p>
            <p className="text-gray-300">
              Tier:{" "}
              <span className="text-white font-semibold">
                {getStars(report.tier)} ({getTierText(report.tier)})
              </span>
            </p>
            <p className="text-gray-300">
              Total Defects:{" "}
              <span className="text-white font-semibold">{report.total_defects}</span>
            </p>
            <p className="text-gray-300">
              Weighted Score:{" "}
              <span className="text-white font-semibold">{report.weighted_score}</span>
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold text-indigo-400 mb-2">Defect Details:</h4>
            {report.defects && report.defects.length > 0 ? (
              <table className="w-full border border-gray-700 text-sm text-gray-200">
                <thead>
                  <tr className="bg-gray-700">
                    <th className="py-2 px-4 border border-gray-600">#</th>
                    <th className="py-2 px-4 border border-gray-600">Tester</th>
                    <th className="py-2 px-4 border border-gray-600">Description</th>
                    <th className="py-2 px-4 border border-gray-600">Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {report.defects.map((defect, index) => (
                    <tr key={index}>
                      <td className="py-2 px-4 border border-gray-700 text-center">{index + 1}</td>
                      <td className="py-2 px-4 border border-gray-700 text-center">
                        {defect.developer_name || "N/A"}
                      </td>
                      <td className="py-2 px-4 border border-gray-700">{defect.description}</td>
                      <td
                        className={`py-2 px-4 border border-gray-700 text-center font-semibold ${defect.severity === "Critical"
                            ? "text-red-500"
                            : defect.severity === "High"
                              ? "text-orange-400"
                              : defect.severity === "Medium"
                                ? "text-yellow-400"
                                : "text-green-400"
                          }`}
                      >
                        {defect.severity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-400">No defects reported.</p>
            )}
          </div>
        </div>
      )}

      {/* All Projects Ranking Table */}
      {allReports.length > 0 && (
        <div className="bg-gray-900 p-5 rounded-xl shadow-md mt-6">
          <h3 className="text-2xl font-semibold text-center text-green-400 mb-4">
            All Projects Quality Ranking
          </h3>
          <table className="w-full border border-gray-700 text-sm text-gray-200">
            <thead>
              <tr className="bg-gray-700">
                <th className="py-2 px-4 border border-gray-600">#</th>
                <th className="py-2 px-4 border border-gray-600">Project Name</th>
                <th className="py-2 px-4 border border-gray-600">Quality (%)</th>
                <th className="py-2 px-4 border border-gray-600">Tier</th>
                <th className="py-2 px-4 border border-gray-600">Defects</th>
              </tr>
            </thead>
            <tbody>
              {allReports.map((proj, index) => (
                <tr key={index}>
                  <td className="py-2 px-4 border border-gray-700 text-center">{index + 1}</td>
                  <td className="py-2 px-4 border border-gray-700">{proj.project_name}</td>
                  <td className="py-2 px-4 border border-gray-700 text-center">
                    {proj.quality_rating.toFixed(2)}%
                  </td>
                  <td className="py-2 px-4 border border-gray-700 text-center">
                    {getStars(proj.tier)} ({proj.tier})
                  </td>
                  <td className="py-2 px-4 border border-gray-700 text-center">
                    {proj.total_defects}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
};

export default ProjectRankingSection;
